import { Capacitor, registerPlugin } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";

import type { ApiEnvironmentName } from "./apiEnvironment";
import type { NativeAdmittedScope } from "../platform/admittedScope";
import type { AppUpdateNotice } from "../platform/Platform";

/**
 * Bridge to the native nav shell (iOS Capacitor shell, Release 1).
 * Native side: ios/App/App/NativeNavPlugin.swift.
 */
export interface NativeNavRoute {
    path: string;
    title: string;
    section?: string;
    /** The page's active-time feature for `path` (`resolveActiveTimeFeature`): the shell
     *  counts a web screen's time under it (`NativeActiveTime`). */
    feature: string;
}

export interface NativeNavMember {
    name: string;
    email: string;
    /** Whether the member holds the stytch_admin role — gates admin-only native UI (Account tab's Environment row). */
    isAdmin: boolean;
}

/** Current backend environment, reported at boot so native pickers can show the active selection. */
export interface NativeNavEnvironment {
    environment: ApiEnvironmentName;
    origin: string;
}

/** Payload for the "setEnvironment" event — a native picker chose a backend environment. */
export interface SetEnvironmentPayload {
    environment: ApiEnvironmentName;
    /** Full origin for "local" (user-specified host/port); absent for staging/prod. */
    origin?: string;
}

export interface NativeNavContext {
    orgSlug: string;
    apiOrigin: string;
    /** Human-readable org display name, when available (see NativeNavBridge.tsx). */
    orgName?: string;
}

export type NativeConnectivityObservation = "reachable" | "transportFailure" | "serverFailure";

/** Organization identity as sent by native org switch — see useCompatOrganization's Organization shape. */
interface NativeNavOrganization {
    organization_id: string;
    organization_slug: string;
    organization_name: string;
}

/** Native org switch payload. The refreshable session token never leaves Keychain. */
export interface OrgSwitchedPayload {
    sessionJwt: string;
    exp: number;
    organization: NativeNavOrganization;
}

interface NativeNavPlugin {
    getTenantScopeCapabilities(): Promise<{ version: number }>;
    installAdmittedScope(options: {
        scope: NativeAdmittedScope & { ownerId: string; sequence: number; apiOrigin: string };
    }): Promise<{ engineAdmissionToken?: string }>;
    clearAdmittedScope(options: {
        scope: { ownerId: string; sequence: number; revokeOffline?: boolean };
    }): Promise<void>;
    /** Reports the current web route so the native sidebar/back stack can track it. */
    reportRoute(route: NativeNavRoute): Promise<void>;
    /** Reports the signed-in member so the native sidebar footer can display it. */
    setMember(member: NativeNavMember): Promise<void>;
    /** Shows/hides the native chrome (sidebar, back/forward) around the WebView. */
    setNavVisible(options: { visible: boolean }): Promise<void>;
    /** Reports the tenant/backend the web app is using so native API calls can be tenant-scoped. */
    setContext(context: NativeNavContext): Promise<void>;
    /** Reports the backend environment resolved at boot so native pickers can show it. */
    reportEnvironment(env: NativeNavEnvironment): Promise<void>;
    /** Feeds real API outcomes into the app-wide native connectivity indicator. */
    reportConnectivity(options: { observation: NativeConnectivityObservation }): Promise<void>;
    /** Fires device haptic feedback (UIFeedbackGenerator) for a web-side interaction. */
    haptic(options: { style: NativeHapticStyle }): Promise<void>;
    /** Presents the native Revision Narrative overlay viewer; resolves once dismissed. */
    openRevisionOverlay(payload: RevisionOverlayPayload): Promise<void>;
    /** The shell's build as it names itself on every request (`X-Primepoint-Client`). */
    getClientIdentity(): Promise<{ client: string }>;
    /** A page request was answered `426 app_update_required`: native shows Update Primepoint. */
    reportUpdateRequired(notice: AppUpdateNotice): Promise<void>;
    addListener(
        eventName: string,
        listenerFunc: (...args: any[]) => any,
    ): Promise<PluginListenerHandle>;
}

/**
 * Payload for `openRevisionOverlay` — everything the native viewer
 * (`RevisionOverlayViewController`, folded into WebTabViewController.swift)
 * needs to render Original/Overlay/Revised without any further round-trip.
 * The three image URLs are the SAME server-precomputed PNGs
 * (`ApiSheetComparison.original_image_url`/`overlay_image_url`/`revised_image_url`)
 * the web's own OverlayViewer.tsx renders — this is a native replacement for
 * that inline viewer, NOT the System A live PDF compositor
 * (`OverlayCompareViewController`/`ViewerApi.fetchOverlayAlignment`), which
 * this does not touch. A mode with no URL (e.g. an added/removed sheet with
 * no "original"/"revised" image) is simply omitted/disabled on the native side.
 */
export interface RevisionOverlayPayload {
    sheetCode: string;
    comparisonTitle: string;
    originalImageUrl?: string;
    overlayImageUrl?: string;
    revisedImageUrl?: string;
}

/** Maps to UIImpactFeedbackGenerator (light/medium), UISelectionFeedbackGenerator
 * (selection), or UINotificationFeedbackGenerator (success) on the native side. */
export type NativeHapticStyle = "light" | "medium" | "selection" | "success";

const NativeNav = registerPlugin<NativeNavPlugin>("NativeNav");

export function isNativeNavAvailable(): boolean {
    return Capacitor.getPlatform() === "ios";
}

/**
 * Fire-and-forget device haptic for web-side interactions (rating a message,
 * markup snap acquisition). No-op outside the native shell — the Vibration API
 * is unavailable in WKWebView, so this bridge is the only way to get haptics.
 */
export function fireHaptic(style: NativeHapticStyle = "light"): void {
    if (!isNativeNavAvailable()) return;
    NativeNav.haptic({ style }).catch(() => {});
}

export function reportRoute(route: NativeNavRoute): Promise<void> {
    return NativeNav.reportRoute(route);
}

export function setMember(member: NativeNavMember): Promise<void> {
    return NativeNav.setMember(member);
}

export function setNavVisible(visible: boolean): Promise<void> {
    return NativeNav.setNavVisible({ visible });
}

export async function setContext(context: NativeNavContext): Promise<void> {
    const { nativeContextProof } = await import("./nativeAdmittedScope");
    return NativeNav.setContext({ ...context, ...nativeContextProof() });
}

export function getTenantScopeCapabilities(): Promise<{ version: number }> {
    return NativeNav.getTenantScopeCapabilities();
}

export function installAdmittedScope(
    scope: NativeAdmittedScope & { ownerId: string; sequence: number; apiOrigin: string },
): Promise<{ engineAdmissionToken?: string }> {
    return NativeNav.installAdmittedScope({ scope });
}

export function clearAdmittedScope(
    ownerId: string,
    sequence: number,
    revokeOffline?: boolean,
): Promise<void> {
    return NativeNav.clearAdmittedScope({
        scope: { ownerId, sequence, ...(revokeOffline ? { revokeOffline } : {}) },
    });
}

export function addAdmissionInvalidatedListener(
    cb: (event: { ownerId: string }) => void,
): Promise<PluginListenerHandle> {
    return NativeNav.addListener("admissionInvalidated", cb);
}

export function reportEnvironment(env: NativeNavEnvironment): Promise<void> {
    return NativeNav.reportEnvironment(env);
}

/** The shell's build, read once at launch; `null` (no identity, never floored) if the shell cannot say. */
export async function getClientIdentity(): Promise<string | null> {
    try {
        const { client } = await NativeNav.getClientIdentity();
        return typeof client === "string" && client ? client : null;
    } catch {
        return null;
    }
}

/** Hands a floored answer to native, which shows the one Update Primepoint screen. */
export function reportUpdateRequired(notice: AppUpdateNotice): void {
    NativeNav.reportUpdateRequired(notice).catch(() => {});
}

let lastReachableReportAt = 0;
let lastTransportFailureReportAt = 0;
let lastServerFailureReportAt = 0;

const REACHABLE_REPORT_THROTTLE_MS = 2_000;
const FAILURE_REPORT_THROTTLE_MS = 1_000;

/**
 * Reports a connectivity observation to native, throttled per observation
 * kind -- a failure storm (e.g. a doomed refresh retry loop hammering a
 * failing endpoint) reports the same identical observation on every failed
 * request, which adds no new information beyond the first. This paces the
 * telemetry IPC only; it never skips or gates the underlying request.
 *
 * Also notifies same-process listeners (see `subscribeConnectivityObservation`)
 * on the same throttled cadence, regardless of platform -- the iOS-only IPC
 * call below is a separate concern from local code (a durable retry queue,
 * say) that wants "connectivity just came back" as a trigger.
 */
export function reportConnectivityObservation(observation: NativeConnectivityObservation): void {
    const now = Date.now();
    if (observation === "reachable") {
        if (now - lastReachableReportAt < REACHABLE_REPORT_THROTTLE_MS) return;
        lastReachableReportAt = now;
    } else if (observation === "transportFailure") {
        if (now - lastTransportFailureReportAt < FAILURE_REPORT_THROTTLE_MS) return;
        lastTransportFailureReportAt = now;
    } else {
        if (now - lastServerFailureReportAt < FAILURE_REPORT_THROTTLE_MS) return;
        lastServerFailureReportAt = now;
    }
    notifyConnectivityObservationListeners(observation);
    if (!isNativeNavAvailable()) return;
    void NativeNav.reportConnectivity({ observation }).catch(() => {});
}

const connectivityObservationListeners = new Set<
    (observation: NativeConnectivityObservation) => void
>();

function notifyConnectivityObservationListeners(observation: NativeConnectivityObservation): void {
    for (const listener of connectivityObservationListeners) listener(observation);
}

/**
 * Subscribes to connectivity observations fed by real API outcomes (axios.ts calls
 * `reportConnectivityObservation` on every native-API response/failure). Platform-
 * independent and throttled identically to the native IPC above. Prefer this over
 * `navigator.onLine`/a browser `online` listener as a "try pending work now" trigger --
 * a local network interface coming up says nothing about whether the backend is
 * reachable, and this app never gates a request on connectivity state, only reacts to
 * observed outcomes (see CLAUDE.md "No connectivity gating").
 */
export function subscribeConnectivityObservation(
    listener: (observation: NativeConnectivityObservation) => void,
): () => void {
    connectivityObservationListeners.add(listener);
    return () => {
        connectivityObservationListeners.delete(listener);
    };
}

/**
 * Opens the native Revision Narrative overlay viewer over the current
 * screen — see `RevisionOverlayPayload`'s doc comment. Callers should only
 * invoke this when `isNativeNavAvailable()`; every other shell keeps the
 * existing inline `OverlayViewer` swap (`RevisionNarrativeReview.tsx`).
 */
export function openRevisionOverlay(payload: RevisionOverlayPayload): Promise<void> {
    return NativeNav.openRevisionOverlay(payload);
}

/** Fires when the native sidebar/back-forward chrome requests a web navigation. */
export function addNavigateListener(
    cb: (data: { path: string }) => void,
): Promise<PluginListenerHandle> {
    return NativeNav.addListener("navigate", cb);
}

/** Fires when the native chrome's logout action is triggered. */
export function addLogoutListener(cb: () => void): Promise<PluginListenerHandle> {
    return NativeNav.addListener("logout", cb);
}

/** Fires when the native Home tab's Ask Marvin box submits a question. */
export function addAskQuestionListener(
    cb: (data: { query: string }) => void,
): Promise<PluginListenerHandle> {
    return NativeNav.addListener("askQuestion", cb);
}

/**
 * Fires when a native environment picker (login-screen three-finger
 * double-tap, or the Account tab's admin-only Environment row) selects a
 * backend environment. The web side persists the override and reloads — see
 * NativeNavBridge.tsx.
 */
export function addSetEnvironmentListener(
    cb: (data: SetEnvironmentPayload) => void,
): Promise<PluginListenerHandle> {
    return NativeNav.addListener("setEnvironment", cb);
}

/** Fires when the native organization switcher (Account tab) completes a switch. */
export function addOrgSwitchedListener(
    cb: (data: OrgSwitchedPayload) => void,
): Promise<PluginListenerHandle> {
    return NativeNav.addListener("orgSwitched", cb);
}

/** Fires when the native Questions tab's message input submits a message. */
export function addSendMessageListener(
    cb: (data: { text: string }) => void,
): Promise<PluginListenerHandle> {
    return NativeNav.addListener("sendMessage", cb);
}

/**
 * Payload for the "attachPhoto" event. `path` is a plain on-device
 * filesystem path (not base64 — MB-scale images can't cross the bridge that
 * way); resolve it via `Capacitor.convertFileSrc` before fetching, mirroring
 * nativeViewer.ts's `fetchViaNativeCache`.
 */
export interface NativeAttachPhotoPayload {
    path: string;
    name: string;
    mimeType: string;
}

/**
 * Fires when the native Questions tab's camera button (WebTabViewController)
 * captures or picks a photo. NativeNavBridge uploads it through the existing
 * FileApi upload path and stages the resulting ApiFile until the next
 * "sendMessage", which carries it along via NATIVE_SEND_MESSAGE_EVENT's
 * `files` field exactly like a desktop attachment send.
 */
export function addAttachPhotoListener(
    cb: (data: NativeAttachPhotoPayload) => void,
): Promise<PluginListenerHandle> {
    return NativeNav.addListener("attachPhoto", cb);
}

/**
 * DOM CustomEvent name NativeNavBridge dispatches on `window` when a native
 * "sendMessage" arrives while already on a conversation route (/c/:id).
 * Conversation.tsx listens for this while mounted and submits the text
 * (plus any staged photo attachments) through the same path the web composer
 * uses. Event `detail` is `{ text, files? }` — `files` are ApiFile entries
 * already uploaded via addAttachPhotoListener's handler.
 */
export const NATIVE_SEND_MESSAGE_EVENT = "primepoint:native-send";

export function addProjectSwitchListener(callback: () => void) {
    return NativeNav.addListener("requestProjectSwitch", callback);
}
